import { differenceInYears } from "date-fns/esm";
import * as _ from "lodash";
import { useInfiniteQuery, useMutation, useQuery } from "react-query";
import {
  addEmplooyeesViaExcel,
  addEmployee,
  addEmployeeExcel,
  bulkCompleteResignation,
  getBulkResignationPreview,
  completeResignation,
  deleteEmployee,
  discardResignation,
  editEmployee,
  patchEmployee,
  employeeResignation,
  generateEmployeeCredentials,
  getAccountingTemplate,
  getAllDocuments,
  getAllSubsidiaryCompanies,
  getDistinctGrantTypes,
  getDistinctLiquidityGroupTypes,
  getEmployeePageDetails,
  getEmployeeHistory,
  validateEmployeeHistoryImport,
  commitEmployeeHistoryImport,
  getCompanyManagers,
  getEmployeeTemplate,
  getEmployeeTransactionDetails,
  getEmployeesOverview,
  getExistingExcelConfigs,
  getGrantPPS,
  getResignationDetails,
  remindOfferedEmployee,
  resignationProcess,
  revertEmployeeresignations,
  revokeOrEnableEmployeeAccess,
  saveEmployeeData,
  transferEmployeeAccess,
  validateAndImportCorrecrtedData,
  verifyAccountingUploadTemplate,
  uploadBulkResignationImport,
} from "../api/Esop";
import { _trimAll } from "../utils/string";
import { queryClient } from "./client";
import { randomEmployeeOverview } from "./placeholders";
import {
  getExsitingGreytipConfigs,
  parseEmployeeMetaData,
  saveConfigCredentials,
  saveExportConfig,
  syncHrms,
  testHrmsConfig,
  updateEmployeeMetaDataBulk,
  validateEmployeeMetaData,
} from "../api/employeeImport";
import { EmployeeStockTransaction } from "../types/VestingTemplate";
import {
  BulkResignationImportDto,
  EmployeeHistoryPage,
  EmployeeHistoryImportCommitResult,
  EmployeeHistoryImportReview,
} from "../types/Employee";
import { adminSignFafLetter, sendFafLetterToEmployee } from "../api/vest";

function refreshEmployeesOverview() {
  return queryClient.invalidateQueries("employeesOverview", {
    refetchActive: true,
    refetchInactive: true,
  });
}

export function useCompanyManagers() {
  return useQuery({
    queryKey: "companyManagers",
    queryFn: getCompanyManagers,
  });
}

export function useEmployees() {
  return useQuery({
    queryKey: "employeesOverview",
    queryFn: async () => {
      const response = await getEmployeesOverview();
      return response.data;
    },
    select: (data) => {
      data.employees.sort(
        (a, b) =>
          new Date(b.createdAt || 0).valueOf() -
          new Date(a.createdAt || 0).valueOf()
      );
      return data.employees.map((employee) => {
        const lastWorkDay = employee.lastDay
          ? new Date(employee.lastDay)
          : new Date();
        return {
          ...employee,
          experience: differenceInYears(new Date(), lastWorkDay),
        };
      });
    },
    placeholderData: randomEmployeeOverview(),
  });
}
export function useEmployeesDepartment() {
  return useQuery({
    queryKey: "employeesOverview",
    queryFn: async () => {
      const response = await getEmployeesOverview();
      return response.data;
    },
    select: (data) => data,
  });
}

export function useEmployeeStatus() {
  return useQuery({
    queryKey: "employeesOverview",
    queryFn: async () => {
      const response = await getEmployeesOverview();
      return response.data;
    },
    select: (data) =>
      _.compact(
        _.uniq(data.employees.map((status) => status.employmentStatus))
      ) || [],
  });
}

export function useEmployeeDepartment() {
  return useQuery({
    queryKey: "employeesOverview",
    queryFn: async () => {
      const response = await getEmployeesOverview();
      return response.data;
    },
    select: ({ employees }) => {
      employees.sort((a, b) => (a.department as any) - (b.department as any));
      const uniqEmployeesByDepartments = _.uniqBy(employees, (employee) =>
        _trimAll(employee.department.toLowerCase())
      );
      return uniqEmployeesByDepartments
        .map((employee) => _trimAll(employee.department))
        .sort();
    },
  });
}

export function useEmployeesJoiningDateRange() {
  return useQuery({
    queryKey: "employeesOverview",
    queryFn: async () => {
      const response = await getEmployeesOverview();
      return response.data;
    },
    select: ({ employees }) => {
      const joiningDates = employees?.map(
        (employee) => new Date(employee.dateOfJoin)
      );
      const dateOfJoinStart = _.min(joiningDates);
      const dateOfJoinEnd = _.max(joiningDates);
      return { dateOfJoinStart, dateOfJoinEnd };
    },
  });
}

export function useAddEmployee() {
  return useMutation({
    mutationKey: "addEmployee",
    mutationFn: addEmployee,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries("esopPlansSummary"),
        queryClient.invalidateQueries("employeePageDetails"),
        refreshEmployeesOverview(),
      ]);
    },
    onError: () => {},
  });
}

export function useEditEmployee() {
  return useMutation({
    mutationKey: "editEmployee",
    mutationFn: editEmployee,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries("employeePageDetails"),
        refreshEmployeesOverview(),
      ]);
    },
    onError: () => {},
  });
}

export function usePatchEmployee() {
  return useMutation({
    mutationKey: "patchEmployee",
    mutationFn: ({
      employeeId,
      request,
    }: {
      employeeId: string;
      request: import("../types/Employee").UpdateEmployeeRequest;
    }) => patchEmployee(employeeId, request),
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries("employeePageDetails"),
        queryClient.invalidateQueries([
          "employeeHistory",
          variables.employeeId,
        ]),
        refreshEmployeesOverview(),
      ]);
    },
  });
}

export function useEmployeeHistory(
  employeeId: string,
  options?: { enabled?: boolean }
) {
  return useInfiniteQuery<EmployeeHistoryPage, Error>({
    queryKey: ["employeeHistory", employeeId],
    queryFn: async ({ pageParam }) =>
      (await getEmployeeHistory(employeeId, pageParam, 20)).data,
    getNextPageParam: (lastPage) => lastPage.nextCursor || undefined,
    enabled: Boolean(employeeId) && options?.enabled !== false,
  });
}

export function useValidateEmployeeHistoryImport() {
  return useMutation<EmployeeHistoryImportReview, Error, string>({
    mutationKey: "validateEmployeeHistoryImport",
    mutationFn: validateEmployeeHistoryImport,
  });
}

export function useCommitEmployeeHistoryImport() {
  return useMutation<
    EmployeeHistoryImportCommitResult,
    Error,
    { cacheId: string; rowIds: string[] }
  >({
    mutationKey: "commitEmployeeHistoryImport",
    mutationFn: ({ cacheId, rowIds }) =>
      commitEmployeeHistoryImport(cacheId, rowIds),
    onSuccess: (result) => {
      result.rows.forEach((row) =>
        queryClient.invalidateQueries(["employeeHistory", row.employeeId])
      );
    },
  });
}

export function useEmployeeTemplate() {
  return useQuery({
    queryKey: "employeeTemplate",
    queryFn: getEmployeeTemplate,
  });
}

export function useAddEmployeeExcel() {
  // name needs to change
  return useMutation({
    mutationKey: "addEmployeeExcel",
    mutationFn: addEmployeeExcel,
    onSuccess: () => {
      queryClient.invalidateQueries("employeesOverview");
      queryClient.refetchQueries("employeesOverview");
    },
    onError: (error: { response: { data: { reason: string } } }) => {},
  });
}

export function useDeleteEmployee() {
  return useMutation({
    mutationKey: "deleteEmployee",
    mutationFn: deleteEmployee,
    onSuccess: () => {
      queryClient.invalidateQueries("employeesOverview");
      queryClient.refetchQueries("employeesOverview");
    },
    onError: () => {},
  });
}

export function useRevokeOrEnableAccess() {
  return useMutation({
    mutationKey: "revokeOrEnableEmployeeAccess",
    mutationFn: revokeOrEnableEmployeeAccess,
    onSuccess: () => {
      queryClient.invalidateQueries("employeesOverview");
      queryClient.invalidateQueries("employeePageDetails");
      queryClient.refetchQueries("employeesOverview");
      queryClient.refetchQueries("employeePageDetails");
    },
  });
}

export function useEmployeeAcceptReminder() {
  return useMutation({
    mutationKey: "remindOfferedEmployee",
    mutationFn: remindOfferedEmployee,
  });
}

export function useEmployeePageDetails(
  empId: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ["employeePageDetails", empId],
    queryFn: async () => (await getEmployeePageDetails(empId)).data,
    select: (data) => data,
    enabled: options?.enabled,
  });
}

export function useEmployeeTransactionDetails(id: string) {
  return useQuery({
    queryKey: ["employeeTransactionDetails", id],
    queryFn: async () => {
      const data = (await getEmployeeTransactionDetails(id)).data as any;
      return data.transactions as EmployeeStockTransaction[];
    },
  });
}

export function useSubsidiaryCompanyNames() {
  return useQuery({
    queryKey: "getAllSubsidiaryCompanyNames",
    queryFn: getAllSubsidiaryCompanies,
  });
}

export function useDistinctGrantTypes() {
  return useQuery({
    queryKey: "distinctGrantTypes",
    queryFn: getDistinctGrantTypes,
  });
}

export function useDistinctLiquidityGroupTypes() {
  return useQuery({
    queryKey: "distinctLiquidityGroupTypes",
    queryFn: getDistinctLiquidityGroupTypes,
    select: (data) => [...data].sort((a, b) => a.localeCompare(b)),
  });
}
export function useGenerateEmployeeCredentials() {
  return useMutation({
    mutationKey: "nomutationKeyBecauseNoCache",
    mutationFn: generateEmployeeCredentials,
    onSuccess: () => {
      queryClient.invalidateQueries("employeesOverview");
      queryClient.invalidateQueries("employeePageDetails");
      queryClient.refetchQueries("employeesOverview");
      queryClient.refetchQueries("employeePageDetails");
    },
  });
}

export function useRevertResignation() {
  return useMutation({
    mutationKey: "revertResignation",
    mutationFn: revertEmployeeresignations,
    onSuccess: () => {
      queryClient.invalidateQueries("employeesOverview");
      queryClient.invalidateQueries("employeePageDetails");
      queryClient.invalidateQueries("allGrants");
      queryClient.refetchQueries("employeesOverview");
      queryClient.refetchQueries("employeePageDetails");
      queryClient.refetchQueries("allGrants");
    },
  });
}

export function useEmployeeResignation() {
  return useMutation({
    mutationKey: "employeeResignation",
    mutationFn: employeeResignation,
    onSuccess: () => {
      queryClient.invalidateQueries("employeesOverview");
      queryClient.invalidateQueries("allGrants");
      queryClient.refetchQueries("employeesOverview");
      queryClient.refetchQueries("allGrants");
      queryClient.invalidateQueries("employeePageDetails");
      queryClient.refetchQueries("employeePageDetails");
    },
    onError: () => {},
  });
}

export function useResignationProcess() {
  return useMutation({
    mutationKey: "resignationProcess",
    mutationFn: resignationProcess,
    onSuccess: () => {
      queryClient.invalidateQueries("documents");
      queryClient.refetchQueries("documents");
      queryClient.invalidateQueries("employeesOverview");
      queryClient.invalidateQueries("allGrants");
      queryClient.refetchQueries("employeesOverview");
      queryClient.refetchQueries("allGrants");
      queryClient.invalidateQueries("employeePageDetails");
      queryClient.refetchQueries("employeePageDetails");
      queryClient.invalidateQueries("getResignationDetails");
    },
    onError: () => {},
  });
}

export function useGetResignationDetails(employeeId: string) {
  return useQuery({
    queryKey: ["getResignationDetails", employeeId],
    queryFn: async () => (await getResignationDetails(employeeId)).data,
  });
}

export function useCompleteResignation() {
  return useMutation({
    mutationKey: "completeResignation",
    mutationFn: completeResignation,
    onSuccess: () => {
      queryClient.invalidateQueries("getResignationDetails");
      queryClient.refetchQueries("getResignationDetails");
      queryClient.invalidateQueries("employeesOverview");
      queryClient.refetchQueries("employeesOverview");
      queryClient.invalidateQueries("allGrants");
      queryClient.refetchQueries("allGrants");
      queryClient.invalidateQueries("employeePageDetails");
      queryClient.refetchQueries("employeePageDetails");
    },
    onError: () => {},
  });
}

export function useBulkCompleteResignation() {
  return useMutation({
    mutationKey: "bulkCompleteResignation",
    mutationFn: bulkCompleteResignation,
    onSuccess: () => {
      queryClient.invalidateQueries("employeesOverview");
      queryClient.refetchQueries("employeesOverview");
      queryClient.invalidateQueries("allGrants");
      queryClient.refetchQueries("allGrants");
      queryClient.invalidateQueries("employeePageDetails");
      queryClient.refetchQueries("employeePageDetails");
    },
    onError: () => {},
  });
}

export function useAdminSignFafLetter() {
  return useMutation({
    mutationKey: "adminSignFafLetter",
    mutationFn: adminSignFafLetter,
    onSuccess: () => {
      queryClient.invalidateQueries("getResignationDetails");
      queryClient.refetchQueries("getResignationDetails");
    },
  });
}

export function useSendFafLetterToEmployee() {
  return useMutation({
    mutationKey: "sendFafLetterToEmployee",
    mutationFn: sendFafLetterToEmployee,
  });
}

export function useEmployeeUploadViaExcel() {
  // names needs to change
  return useMutation({
    mutationKey: "employeeUploadViaExcel",
    mutationFn: addEmplooyeesViaExcel,
    onSuccess: () => {
      queryClient.invalidateQueries("employeesOverview");
      queryClient.refetchQueries("employeesOverview");
    },
    onError: (error: {
      response: { data: { reason: string; errorRaw: string } };
    }) => {},
  });
}
export function useSaveEmployeeData() {
  return useMutation({
    mutationKey: "saveEmployeeData",
    mutationFn: saveEmployeeData,
    onSuccess: () => {
      queryClient.invalidateQueries("employeesOverview");
      queryClient.refetchQueries("employeesOverview");
    },
    onError: (error: { response: { data: { reason: string } } }) => {},
  });
}
export function useGetEmployeeUploadConfigs() {
  return useQuery({
    queryKey: "EmployeeUIloadConfigs",
    queryFn: getExistingExcelConfigs,
    onError: (error: { response: { data: { reason: string } } }) => {},
  });
}
export function useValidateAndImportEmployee() {
  return useMutation({
    mutationKey: "validateAndImportEmployee",
    mutationFn: validateAndImportCorrecrtedData,
    onSuccess: () => {
      queryClient.invalidateQueries("allGrants");
      queryClient.invalidateQueries("esopPlansSummary");
      queryClient.invalidateQueries("employeesOverview");
      queryClient.invalidateQueries("employeePageDetails");
      queryClient.refetchQueries("allGrants");
      queryClient.refetchQueries("employeesOverview");
      queryClient.refetchQueries("employeePageDetails");
      queryClient.refetchQueries("esopPlansSummary");
    },
    onError: (error: { response: { data: { reason: string } } }) => {},
  });
}
export function useDiscardResignation() {
  return useMutation({
    mutationKey: "discardResignation",
    mutationFn: discardResignation,
    onSuccess: () => {},
    onError: () => {},
  });
}

export function useDocuments() {
  return useQuery({
    queryKey: "documents",
    queryFn: async () => {
      const response = await getAllDocuments();
      return response.data;
    },
    staleTime: 1000 * 60 * 10,
  });
}
export function useGetGreyTipConfigs() {
  return useQuery({
    queryKey: "getGreyTipConfigs",
    queryFn: getExsitingGreytipConfigs,
    retry: false,
  });
}

export function useTestHrmsConfig() {
  return useMutation({
    mutationKey: "testHrmsConfig",
    mutationFn: testHrmsConfig,
  });
}

export function useSaveConfigCredentials() {
  return useMutation({
    mutationKey: "saveConfigCredentials",
    mutationFn: ({
      configId,
      credentials,
    }: {
      configId: string;
      credentials: any;
    }) => saveConfigCredentials(configId, credentials),
  });
}

export function usesyncHrms() {
  return useMutation({
    mutationKey: "syncHrms",
    mutationFn: syncHrms,
    onSuccess: () => {
      queryClient.invalidateQueries("employeesOverview");
      queryClient.refetchQueries("employeesOverview");
    },
  });
}

export function useTransferEmployeeAccess() {
  return useMutation({
    mutationKey: "transferAccess",
    mutationFn: transferEmployeeAccess,
    onSuccess: () => {
      queryClient.invalidateQueries("employeePageDetails");
      queryClient.refetchQueries("employeePageDetails");
    },
  });
}

export function useVerifyAccountingUploadTemplate() {
  return useMutation({
    mutationKey: "accountingTemplateValidate",
    mutationFn: verifyAccountingUploadTemplate,
    onSuccess: () => {},
    onError: () => {},
  });
}

export function useVerifyEmployeeMetaDataUploadTemplate() {
  return useMutation({
    mutationKey: "employeeMetaDataTemplateValidate",
    mutationFn: validateEmployeeMetaData,
    onSuccess: () => {},
    onError: () => {},
  });
}

export function useParseEmployeeMetaData() {
  return useMutation({
    mutationKey: "parseEmployeeMetaData",
    mutationFn: parseEmployeeMetaData,
    onSuccess: () => {},
    onError: () => {},
  });
}

export function useUpdateEmployeeMetaDataBulk() {
  return useMutation({
    mutationKey: "updateEmployeeMetaDataBulk",
    mutationFn: updateEmployeeMetaDataBulk,
    onSuccess: () => {},
    onError: () => {},
  });
}

export function useGetGrantPPS(date: string) {
  return useQuery({
    queryKey: ["getGrantPPS", date],
    queryFn: async () => (await getGrantPPS(date)).data,
  });
}

export function useSaveNewExportConfig() {
  return useMutation({
    mutationKey: "saveNewExportConfig",
    mutationFn: saveExportConfig,
    onSuccess: () => {
      queryClient.invalidateQueries("companyConfigs");
      queryClient.refetchQueries("companyConfigs");
    },
  });
}

export function useUploadBulkResignationImport() {
  return useMutation({
    mutationKey: "uploadBulkResignationImport",
    mutationFn: (dto: BulkResignationImportDto) =>
      uploadBulkResignationImport(dto),
    onSuccess: () => {
      queryClient.invalidateQueries("employeesOverview");
    },
  });
}

export function useGetBulkResignationPreview() {
  return useMutation({
    mutationKey: "getBulkResignationPreview",
    mutationFn: getBulkResignationPreview,
  });
}
